import { useCallback, useEffect, useState } from 'react';
import { HourlyBars, PlayersLine } from '../components/Charts';
import { api, ApiError, type ReportSettings, type StatsReport } from '../lib/api';
import { useElena } from '../state';

const ranges = [1, 7, 14] as const;

const hours = (min: number) =>
  min >= 60 ? `${(Math.round((min / 60) * 10) / 10).toString().replace('.', ',')} h` : `${min} min`;

const when = (iso: string) =>
  new Date(iso).toLocaleString('sr-Latn-RS', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Belgrade',
  });

const dayLabel = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('sr-Latn-RS', {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
    timeZone: 'UTC',
  });
};

export function Stats() {
  const { status } = useElena();
  const [days, setDays] = useState<(typeof ranges)[number]>(7);
  const [data, setData] = useState<{ stats: StatsReport; summary: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.stats(days));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ne mogu da učitam statistiku.');
    }
  }, [days]);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 120_000);
    return () => window.clearInterval(t);
  }, [load]);

  const s = data?.stats;
  const name = status?.server.name ?? 'Server';

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Statistika</h1>
          <p className="muted">
            {name}: igrači, padovi i najbolje vreme za restart. Elena beleži stanje svaka 2 minuta.
          </p>
        </div>
        <div className="segmented" role="radiogroup" aria-label="Period">
          {ranges.map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={days === r}
              className={days === r ? 'is-on' : ''}
              onClick={() => setDays(r)}
            >
              {r === 1 ? '24 h' : `${r} dana`}
            </button>
          ))}
        </div>
      </header>

      {error && <p className="notice">{error}</p>}

      {s && s.samples === 0 && (
        <section className="card">
          <p>Još nema podataka. Prvi grafikoni se vide posle desetak minuta rada.</p>
        </section>
      )}

      {s && s.samples > 0 && (
        <>
          <div className="stat-tiles">
            <Tile
              label="Najviše igrača"
              value={s.peak ? String(s.peak.players) : '—'}
              hint={s.peak ? when(s.peak.at) : ''}
            />
            <Tile
              label="Prosek"
              value={s.average !== null ? String(s.average).replace('.', ',') : '—'}
              hint="kad server radi"
            />
            <Tile
              label="Radio"
              value={
                s.uptime === null
                  ? '—'
                  : s.uptime >= 1
                    ? '100%'
                    : `${(Math.floor(s.uptime * 1000) / 10).toString().replace('.', ',')}%`
              }
              hint={s.incidents.length ? `${s.incidents.length}× pao` : 'nije padao'}
            />
            <Tile
              label="Restart"
              value={
                s.bestRestartHour ? `${String(s.bestRestartHour.hour).padStart(2, '0')}:00` : '—'
              }
              hint={
                s.bestRestartHour
                  ? `najmirnije · prosek ${String(s.bestRestartHour.average).replace('.', ',')}`
                  : ''
              }
              accent
            />
          </div>

          <section className="card" aria-labelledby="st-24h">
            <header className="card-head">
              <h2 id="st-24h">Igrači, poslednja 24 h</h2>
              <span className="muted small">
                <span className="legend-gap" aria-hidden="true" /> server ne odgovara
              </span>
            </header>
            <PlayersLine series={s.series} label="Broj igrača u poslednja 24 sata" />
          </section>

          <div className="grid-2">
            <section className="card" aria-labelledby="st-hourly">
              <header className="card-head">
                <h2 id="st-hourly">Prosek po satima</h2>
                <span className="muted small">
                  {s.days === 1 ? 'poslednja 24 h' : `poslednjih ${s.days} dana`}
                </span>
              </header>
              <HourlyBars
                hourly={s.hourly}
                best={s.bestRestartHour?.hour ?? null}
                label="Prosečan broj igrača po satu"
              />
              {s.bestRestartHour && (
                <p className="muted small">
                  <span className="dot brass" /> Najmanje igrača je oko{' '}
                  {String(s.bestRestartHour.hour).padStart(2, '0')}:00 — tad restart smeta najmanjem
                  broju ljudi.
                </p>
              )}
            </section>

            <section className="card" aria-labelledby="st-top">
              <header className="card-head">
                <h2 id="st-top">Ko najviše igra</h2>
              </header>
              {s.top.length === 0 ? (
                <p className="muted">Još niko nije zabeležen.</p>
              ) : (
                <ol className="rank">
                  {s.top.map((p) => (
                    <li key={p.name}>
                      <span className="rank-name">{p.name}</span>
                      <span className="rank-bar" aria-hidden="true">
                        <span
                          style={{ width: `${(p.minutes / (s.top[0]?.minutes || 1)) * 100}%` }}
                        />
                      </span>
                      <span className="rank-value">{hours(p.minutes)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>

          <div className="grid-2">
            <section className="card" aria-labelledby="st-days">
              <header className="card-head">
                <h2 id="st-days">Po danima</h2>
              </header>
              <div className="table-wrap">
                <table className="table compact">
                  <thead>
                    <tr>
                      <th scope="col">Dan</th>
                      <th scope="col">Najviše</th>
                      <th scope="col">Prosek</th>
                      <th scope="col">Igrača</th>
                      <th scope="col">Pao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...s.daily].reverse().map((d) => (
                      <tr key={d.day}>
                        <th scope="row">{dayLabel(d.day)}</th>
                        <td>{d.peak}</td>
                        <td>{String(d.average).replace('.', ',')}</td>
                        <td>{d.uniquePlayers}</td>
                        <td className={d.offlineMinutes ? 'bad' : ''}>
                          {d.offlineMinutes ? hours(d.offlineMinutes) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="card" aria-labelledby="st-down">
              <header className="card-head">
                <h2 id="st-down">Padovi</h2>
              </header>
              {s.incidents.length === 0 ? (
                <p className="muted">Server nije padao u ovom periodu.</p>
              ) : (
                <ul className="list">
                  {[...s.incidents].reverse().map((i) => (
                    <li key={i.from} className="list-row">
                      <span className="dot off" />
                      <span className="grow">
                        {when(i.from)}
                        <span className="muted small block">
                          {i.to ? `do ${when(i.to)}` : 'još ne odgovara'}
                        </span>
                      </span>
                      <span className="tag tag-failed">{hours(i.minutes)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      <ReportCard />
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint: string;
  accent?: boolean;
}) {
  return (
    <div className={`card stat-tile${accent ? ' is-accent' : ''}`}>
      <span className="muted small">{label}</span>
      <span className="stat-value">{value}</span>
      <span className="muted small">{hint || ' '}</span>
    </div>
  );
}

function ReportCard() {
  const [report, setReport] = useState<ReportSettings | null>(null);
  const [time, setTime] = useState('08:00');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .report()
      .then((r) => {
        setReport(r.report);
        setTime(r.report.time);
      })
      .catch(() => undefined);
  }, []);

  const save = async (enabled: boolean, t: string) => {
    try {
      const r = await api.saveReport(enabled, t);
      setReport(r.report);
      setNote('Sačuvano.');
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'Nije sačuvano.');
    }
  };

  if (!report) return null;
  return (
    <section className="card" aria-labelledby="st-report">
      <header className="card-head">
        <h2 id="st-report">Jutarnji izveštaj</h2>
        <span className={`pill ${report.enabled ? 'ok' : 'idle'}`}>
          {report.enabled ? `svaki dan u ${report.time}` : 'isključen'}
        </span>
      </header>
      <p className="muted">
        Jedna notifikacija na telefon: koliko je igrača bilo, da li je server padao, ko je najviše
        igrao, koliko je Claude potrošio i koliko podsetnika imaš tog dana. Ne troši Claude.
      </p>
      <div className="row gap-8 wrap">
        <label className="toggle">
          <input
            type="checkbox"
            checked={report.enabled}
            onChange={(e) => void save(e.target.checked, time)}
          />
          Šalji svako jutro
        </label>
        <label className="row gap-8">
          <span className="muted">u</span>
          <input
            type="time"
            className="field small-field time-field"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            onBlur={() =>
              time !== report.time && /^\d{2}:\d{2}$/.test(time) && void save(report.enabled, time)
            }
            aria-label="Vreme izveštaja"
          />
        </label>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              setReport((await api.sendReport()).report);
              setNote('Poslato na telefon.');
            } catch (err) {
              setNote(err instanceof ApiError ? err.message : 'Nije poslato.');
            } finally {
              setBusy(false);
            }
          }}
        >
          Pošalji sada
        </button>
        {note && (
          <span className="muted small" role="status">
            {note}
          </span>
        )}
      </div>
      {report.last && (
        <blockquote className="report-preview">
          <span className="strong">{report.last.title}</span>
          <span>{report.last.text}</span>
          <span className="muted small">poslato {when(report.last.sentAt)}</span>
        </blockquote>
      )}
    </section>
  );
}
